import { useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { command, type GrantScope, type VaultService } from '@/services/vault/vault.http'
import type { ClientTokens } from '@/services/vault/auth'

export const TERMS = 'vault-consent-v2'
export const RETENTION =
  'The former firm retains read-only access to the exact data versions it touched and its closed workspaces. Future versions and the new firm’s private work are excluded.'
export const RECORD_ACTIONS = [
  'profile.read',
  'profile.update',
  'document.read',
  'document.download',
  'document.upload',
  'output.read',
  'output.download',
  'output.publish',
]
export const WORK_ACTIONS = [
  'workspace.create',
  'workspace.update',
  'workspace.assign',
  'workspace.process',
  'workspace.file',
  'workspace.delete',
  'workspace.restore',
  'workspace.communicate',
  'workspace.charge',
]
export const ACTIONS = [...RECORD_ACTIONS, ...WORK_ACTIONS]

export const actionLabel = (action: string) =>
  ({
    'profile.read': 'View profile',
    'profile.update': 'Update profile',
    'document.read': 'View client documents',
    'document.download': 'Download client documents',
    'document.upload': 'Upload client documents',
    'output.read': 'View professional outputs',
    'output.download': 'Download professional outputs',
    'output.publish': 'Publish professional outputs',
    'workspace.create': 'Create professional cases and records',
    'workspace.update': 'Update professional cases and records',
    'workspace.assign': 'Assign staff to professional work',
    'workspace.process': 'Process documents and case data',
    'workspace.file': 'File returns and record acknowledgements',
    'workspace.delete': 'Delete professional records',
    'workspace.restore': 'Restore professional records',
    'workspace.communicate': 'Send case messages and reminders',
    'workspace.charge': 'Create charges and payment requests',
  })[action] ?? action

export function Field({
  label,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="block text-sm text-text-secondary space-y-1">
      <span>{label}</span>
      <input {...props} className="input input-bordered w-full" />
    </label>
  )
}
export function ServiceSelect({
  value,
  onChange,
}: {
  value: VaultService
  onChange: (value: VaultService) => void
}) {
  return (
    <label className="block text-sm text-text-secondary space-y-1">
      <span>Service</span>
      <select
        className="input input-bordered w-full"
        value={value}
        onChange={event => onChange(event.target.value as VaultService)}
      >
        <option value="income_tax">Income tax</option>
        <option value="gst">GST</option>
        <option value="finance">Finance</option>
      </select>
    </label>
  )
}
export function ScopeFields({
  scope,
  onChange,
}: {
  scope: GrantScope
  onChange: (value: GrantScope) => void
}) {
  return (
    <fieldset className="space-y-3">
      <legend className="font-medium text-text-primary">Exact access scope</legend>
      <p className="text-sm text-text-secondary">
        Select the permissions you authorize for the categories and financial years below. Viewing
        client records does not authorize professional work.
      </p>
      {[
        { title: 'Client records', actions: RECORD_ACTIONS },
        { title: 'Professional work', actions: WORK_ACTIONS },
      ].map(group => (
        <fieldset key={group.title} className="space-y-2">
          <legend className="text-sm font-medium text-text-primary">{group.title}</legend>
          <div className="flex flex-wrap gap-3">
            {group.actions.map(action => (
              <label key={action} className="flex gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={scope.actions.includes(action)}
                  onChange={event =>
                    onChange({
                      ...scope,
                      actions: event.target.checked
                        ? [...scope.actions, action]
                        : scope.actions.filter(value => value !== action),
                    })
                  }
                />
                {actionLabel(action)}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      <Field
        label="Categories (comma separated; include portable_profile for profile access)"
        value={scope.categories.join(',')}
        required
        onChange={event => onChange({ ...scope, categories: event.target.value.split(',') })}
      />
      <Field
        label="Financial years (YYYY-YY; explicitly include undated for profiles)"
        value={scope.financial_years.join(',')}
        required
        onChange={event => onChange({ ...scope, financial_years: event.target.value.split(',') })}
      />
    </fieldset>
  )
}
export const normalizeScope = (scope: GrantScope) => ({
  ...scope,
  categories: scope.categories.map(value => value.trim()).filter(Boolean),
  financial_years: scope.financial_years.map(value => value.trim()).filter(Boolean),
})

/** Same form payload keeps its key after lost acknowledgement; keys never leave memory except in commands. */
export function useCommand() {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('')
  const keys = useRef(new Map<string, string>())
  const key = (payload: unknown) => {
    const digest = JSON.stringify(payload)
    if (!keys.current.has(digest)) keys.current.set(digest, crypto.randomUUID())
    return keys.current.get(digest)!
  }
  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    setError('')
    setMessage('')
    try {
      await action()
      setMessage('Request completed.')
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Request failed')
    } finally {
      setBusy(false)
    }
  }
  return { busy, error, message, idempotencyKey: key, run }
}
export function CommandStatus({ error, message }: { error: string; message: string }) {
  return (
    <>
      {error && (
        <p role="alert" className="text-error text-sm">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="text-text-secondary text-sm">
          {message}
        </p>
      )}
    </>
  )
}
export function useStepUp() {
  const { user, adoptTokens } = useAuth()
  return async (code: string) => {
    if (!adoptTokens) throw new Error('Session update unavailable')
    const result = await command<ClientTokens>(
      user?.authScope === 'client_vault' ? '/auth/client/step-up' : '/auth/firm/step-up',
      { mfa_code: code.trim() }
    )
    await adoptTokens(result)
  }
}
